package er.prateek.crossly.ui.components

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game

/**
 * Pill-group selector for small mutually exclusive choices (theme mode, palette).
 * The selected segment fills with the accent and cross-fades rather than snapping,
 * which keeps Settings feeling like part of the game. A null [selected] highlights nothing,
 * for a setting that currently has no single value.
 */
@Composable
fun <T : Any> SegmentedControl(
    options: List<T>,
    selected: T?,
    onSelect: (T) -> Unit,
    modifier: Modifier = Modifier,
    label: @Composable (T) -> String,
) {
    val colors = MaterialTheme.game
    // Intrinsic height lets every segment match the tallest one, so a label that wraps
    // (e.g. "Still needed" on a narrow phone) doesn't leave its neighbours top-aligned.
    Row(
        modifier = modifier
            .height(IntrinsicSize.Min)
            .clip(RoundedCornerShape(16.dp))
            .background(colors.ink.copy(alpha = 0.06f))
            .border(1.dp, colors.cardBorder, RoundedCornerShape(16.dp))
            .padding(Spacing.xs),
        horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
    ) {
        options.forEach { option ->
            val isSelected = option == selected
            val background by animateColorAsState(
                if (isSelected) colors.accent else colors.accent.copy(alpha = 0f),
                tween(220),
                label = "segmentBg",
            )
            val content by animateColorAsState(
                if (isSelected) colors.onAccent else colors.inkMuted,
                tween(220),
                label = "segmentFg",
            )
            Box(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight()
                    .clip(RoundedCornerShape(12.dp))
                    .background(background)
                    .clickable(onClick = { onSelect(option) })
                    .padding(vertical = 10.dp, horizontal = Spacing.sm),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    text = label(option),
                    style = MaterialTheme.typography.labelMedium,
                    color = content,
                    textAlign = TextAlign.Center,
                )
            }
        }
    }
}

/** Row alignment helper so callers don't repeat the vertical-centering boilerplate. */
@Composable
fun CenteredRow(
    modifier: Modifier = Modifier,
    spacing: androidx.compose.ui.unit.Dp = Spacing.sm,
    content: @Composable androidx.compose.foundation.layout.RowScope.() -> Unit,
) {
    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(spacing),
        verticalAlignment = Alignment.CenterVertically,
        content = content,
    )
}
